import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './GettingStartedGuide.css';

const GettingStartedGuide = ({ onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const navigate = useNavigate();

    const steps = [
        {
            title: "Welcome to Spreadsheetr! 🎉",
            content: (
                <div className="guide-welcome">
                    <div className="guide-logo">
                        <svg width="80" height="80" viewBox="0 0 100 100" fill="none">
                            <defs>
                                <linearGradient id="guideGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#22d3ee" />
                                    <stop offset="50%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                            </defs>
                            <rect x="10" y="15" width="80" height="72" rx="12" ry="12" fill="url(#guideGradient)" />
                            <rect x="15" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                            <rect x="40" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                            <rect x="65" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                            <rect x="15" y="34" width="20" height="10" rx="2" fill="white" />
                            <rect x="40" y="34" width="20" height="10" rx="2" fill="white" />
                            <rect x="65" y="34" width="20" height="10" rx="2" fill="white" />
                            <rect x="15" y="48" width="20" height="10" rx="2" fill="white" />
                            <rect x="40" y="48" width="20" height="10" rx="2" fill="white" />
                            <rect x="65" y="48" width="20" height="10" rx="2" fill="white" />
                            <rect x="15" y="62" width="20" height="10" rx="2" fill="white" />
                            <rect x="40" y="62" width="20" height="10" rx="2" fill="white" />
                            <rect x="65" y="62" width="20" height="10" rx="2" fill="white" />
                            <path d="M50 53 L65 68 L95 30" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                    </div>
                    <h2>Stop Worrying About Excel Breaking Your Business</h2>
                    <p>Spreadsheetr transforms your critical Excel files into bulletproof, reusable calculation templates.</p>
                    <div className="guide-benefits">
                        <div className="benefit-item">
                            <span className="benefit-icon">✓</span>
                            <span>No more "Excel person" dependency</span>
                        </div>
                        <div className="benefit-item">
                            <span className="benefit-icon">✓</span>
                            <span>Zero calculation errors</span>
                        </div>
                        <div className="benefit-item">
                            <span className="benefit-icon">✓</span>
                            <span>Fully auditable and compliant</span>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "How Spreadsheetr Works",
            content: (
                <div className="guide-how-it-works">
                    <div className="workflow-steps">
                        <div className="workflow-step">
                            <div className="workflow-number">1</div>
                            <div className="workflow-content">
                                <h3>Upload Your Excel File</h3>
                                <p>Upload any Excel file with formulas. We automatically extract the calculation logic.</p>
                                <div className="workflow-example">
                                    <code>Example: school-fees.xlsx</code>
                                </div>
                            </div>
                        </div>
                        <div className="workflow-arrow">↓</div>
                        <div className="workflow-step">
                            <div className="workflow-number">2</div>
                            <div className="workflow-content">
                                <h3>Review & Activate</h3>
                                <p>Check the detected inputs and formulas. Activate when ready.</p>
                                <div className="workflow-example">
                                    <span className="badge badge-success">Template Ready</span>
                                </div>
                            </div>
                        </div>
                        <div className="workflow-arrow">↓</div>
                        <div className="workflow-step">
                            <div className="workflow-number">3</div>
                            <div className="workflow-content">
                                <h3>Run Calculations</h3>
                                <p>Upload new data anytime. Get instant, error-free results.</p>
                                <div className="workflow-example">
                                    <span className="badge badge-primary">500 students processed</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Your Dashboard",
            content: (
                <div className="guide-dashboard">
                    <p>Your dashboard shows everything at a glance:</p>
                    <div className="dashboard-features">
                        <div className="feature-box">
                            <div className="feature-icon">📊</div>
                            <h4>Active Templates</h4>
                            <p>Ready-to-use calculation templates</p>
                        </div>
                        <div className="feature-box">
                            <div className="feature-icon">▶️</div>
                            <h4>Recent Runs</h4>
                            <p>Your calculation history</p>
                        </div>
                        <div className="feature-box">
                            <div className="feature-icon">📝</div>
                            <h4>Recent Activity</h4>
                            <p>What's happening in your organization</p>
                        </div>
                    </div>
                    <div className="guide-tip">
                        <strong>💡 Tip:</strong> Start by creating your first template in the Governance Workspace
                    </div>
                </div>
            )
        },
        {
            title: "Key Features",
            content: (
                <div className="guide-features">
                    <div className="feature-list">
                        <div className="feature-item">
                            <div className="feature-header">
                                <span className="feature-emoji">🏛️</span>
                                <h4>Governance Workspace</h4>
                            </div>
                            <p>Upload Excel files and create templates. Admin-only area for setting up calculations.</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-header">
                                <span className="feature-emoji">🚀</span>
                                <h4>Operation Portal</h4>
                            </div>
                            <p>Run calculations with active templates. Upload data, execute, download results.</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-header">
                                <span className="feature-emoji">🎨</span>
                                <h4>Output Formatting</h4>
                            </div>
                            <p>Sort, group, and format your results. Perfect for reports by class, department, or category.</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-header">
                                <span className="feature-emoji">📋</span>
                                <h4>Audit Trail</h4>
                            </div>
                            <p>Every calculation is logged. See who ran what, when, and with what data.</p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Ready to Get Started?",
            content: (
                <div className="guide-ready">
                    <h2>You're all set! 🎯</h2>
                    <p>Here's what to do next:</p>
                    <div className="next-steps">
                        <div className="next-step-card" onClick={() => {
                            navigate('/app/governance');
                            onClose();
                        }}>
                            <div className="next-step-icon">📤</div>
                            <h4>Create A Template</h4>
                            <p>Upload an Excel file to get started</p>
                            <button className="btn btn-primary">Go to Governance →</button>
                        </div>
                        <div className="next-step-card" onClick={() => {
                            navigate('/app/portal');
                            onClose();
                        }}>
                            <div className="next-step-icon">▶️</div>
                            <h4>Run a Calculation</h4>
                            <p>Try the Operation Portal</p>
                            <button className="btn btn-secondary">Go to Portal →</button>
                        </div>
                    </div>
                    <div className="guide-help">
                        <p>Need help? Check out our documentation or contact support.</p>
                    </div>
                </div>
            )
        }
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onClose();
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSkip = () => {
        onClose();
    };

    return (
        <div className="getting-started-overlay">
            <div className="getting-started-modal">
                <button className="guide-close" onClick={handleSkip} aria-label="Close guide">
                    ×
                </button>

                <div className="guide-progress">
                    {steps.map((_, index) => (
                        <div
                            key={index}
                            className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                            onClick={() => setCurrentStep(index)}
                        />
                    ))}
                </div>

                <div className="guide-content">
                    <h1 className="guide-title">{steps[currentStep].title}</h1>
                    <div className="guide-body">
                        {steps[currentStep].content}
                    </div>
                </div>

                <div className="guide-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={handleSkip}
                    >
                        Skip Tour
                    </button>
                    <div className="guide-navigation">
                        {currentStep > 0 && (
                            <button
                                className="btn btn-secondary"
                                onClick={handlePrevious}
                            >
                                ← Previous
                            </button>
                        )}
                        <button
                            className="btn btn-primary"
                            onClick={handleNext}
                        >
                            {currentStep < steps.length - 1 ? 'Next →' : 'Get Started'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GettingStartedGuide;
